import { useCallback } from 'react';
import { millisecondsInSecond } from 'date-fns';

import { appStore } from '@kaltura-ott/tvpil-shared';

import { tryToRestorePlayback } from 'components/widgets/Player/handlers/handleError/helpers/tryToRestorePlayback/tryToRestorePlayback';
import { playerStorage } from 'components/widgets/Player/storages';
import { useErrorScreenContext } from 'context/error/error';
import { CommonError } from 'context/error/types';
import { useDetectLocation } from 'hooks/common/useDetectLocation';
import networkService from 'services/networkService';
import { getErrorParams } from 'utils/getErrorParams';
import { isNetworkErrorCode } from 'utils/isNetworkErrorCode';

import { retryConnection } from '../utils/errorTimers';

export function useHandleFullScreenError() {
  const errorScreenContext = useErrorScreenContext();
  const { checkInterval } = appStore.getConnectivityConfiguration();
  const checkIntervalInSeconds = checkInterval / millisecondsInSecond;
  const { isPlayerLocation } = useDetectLocation();
  const isFullPlayerScreen = isPlayerLocation(window.location.pathname);

  // temporary Magic solution until full transition to the rhino instead of playoustore and global error handling 
  const extraActionOnRetry = useCallback(async () => {
    errorScreenContext.clearError();
    await tryToRestorePlayback();
    playerStorage.playerPlay(); // starts playing cause after getting an error we've paused the video
  }, [])
  //

  return async (commonError: CommonError, id: number, isErrorFromTvpil = true) => {
    const isNoConnection = isNetworkErrorCode(commonError.code) && !(await networkService.isNetworkConnection());
    const errorParams = getErrorParams({
      commonError,
      isNoConnection,
      isErrorFromTvpil,
      stopLoader: errorScreenContext.stopLoadingError,
      id,
      extraActionOnRetry: isFullPlayerScreen ? extraActionOnRetry : undefined
    });

    if (isNoConnection) {
      if (isFullPlayerScreen) {
        // to avoid double calling of error context (by tvpil and by player)
        playerStorage.setIsNoConnectionErrorFired(true);
        // to avoid playback in the background of the error screen (when get player errors with severity 1)
        playerStorage.playerPause();
      }

      errorScreenContext.setError({ ...errorParams, checkConnectionTimer: checkIntervalInSeconds });
      retryConnection(errorParams, errorScreenContext);
    } else {
      errorScreenContext.setError(errorParams);
    }
  };
}
